'use client';

import { TransitioningContext } from '@/components/TransitioningContext/TransitioningContext';
import { useIdentifyBrowserSession } from '@/hooks/useIdentifyBrowserSession';
import { fallbackLng } from '@/i18n-config';
import {
  ActionCard,
  AuiContainer,
  RadioButton,
  space,
} from '@/lib/mx-asurion-ui-react';
import { startFullStoryRecording } from '@/utils/fullStory-utils';
import {
  PartnerIdentifier,
  PresentationIdentifier,
  getProductConfig,
  getProductConfigsForPresentationRouteByPartner,
} from '@sales-domain/product-config';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useFormState } from 'react-dom';
import { submitPlanSelection } from './actions';
import './PlanSelection.css';

type PlanSelectionProps = {
  nextStep?: string;
  productDetails: Record<
    string,
    { name: string; price: string; productDetails: string }
  >;
};

type PlanDetails = {
  productSku?: string;
  name?: string;
  price?: string;
  productDetails?: string;
};

export default function PlanSelection(props: PlanSelectionProps) {
  const router = useRouter();
  const [selectedRadio, setSelectedRadio] = useState('PDPF');
  const params = useParams();
  const presentation = PresentationIdentifier.PrePaid;
  const nextStep = `/${params.partner}/${presentation}/${props.nextStep}`;
  const searchParams = useSearchParams();

  // action
  const [state, formAction] = useFormState(submitPlanSelection, {
    success: false,
    errors: [],
  });

  // context
  const { transitioning, setTransitioning } = useContext(TransitioningContext);

  // analytics
  useIdentifyBrowserSession();

  // To set order of radio buttons using price
  function getPlanOptions(
    partner?: string | string[],
    presentation?: string,
  ): PlanDetails[] | undefined {
    if (partner && presentation) {
      const productConfigs = getProductConfigsForPresentationRouteByPartner(
        partner as PartnerIdentifier,
        presentation as PresentationIdentifier,
      );

      return productConfigs.sort((a: PlanDetails, b: PlanDetails) => {
        if (!a.price || !b.price) {
          throw new Error(`price is missing`);
        }
        if (a.price > b.price) {
          return 1;
        }
        if (a.price < b.price) {
          return -1;
        }
        return 0;
      });
    }
  }

  // hooks
  const sortedPlanOptions = useMemo(
    () => getPlanOptions(params.partner, presentation),
    [params.partner, presentation],
  );

  const onRadioSelect = useCallback((e: any) => {
    const target = e.target as HTMLInputElement;

    setSelectedRadio(target.value);
  }, []);

  useEffect(() => {
    startFullStoryRecording();
  }, []);

  useEffect(() => {
    if (state.success) {
      setTransitioning(true);
      const config = getProductConfig(selectedRadio);
      const product =
        config.channelConfigs.DirectToConsumer?.appRouteConfig
          .productIdentifier;
      if (params.lang !== fallbackLng) {
        router.push(`/${params.lang}/${product}/${nextStep}?${searchParams}`);
      } else {
        router.push(`/${product}/${nextStep}?${searchParams}`);
      }
    }
  }, [
    state,
    searchParams,
    router,
    params,
    presentation,
    props.nextStep,
    setTransitioning,
    selectedRadio,
    nextStep,
  ]);

  useEffect(() => {
    setTransitioning(false);
  }, [setTransitioning]);

  return (
    <form
      id="plan-selection"
      action={formAction}
      onSubmit={() => {
        setTransitioning(true);
      }}
      className="max-sm:px-4"
    >
      <AuiContainer gap={space[300]} role="radiogroup">
        {sortedPlanOptions &&
          sortedPlanOptions.map((item) => {
            if (!item.productSku) {
              return null;
            }

            const productInfo = props.productDetails[item.productSku] ?? {};
            return (
              <div key={item.productSku} className="flex flex-col">
                <input type="hidden" name="productSku" value={selectedRadio} />
                <RadioButton
                  className="text-[#000000]"
                  key={item.productSku}
                  label={`${productInfo.name ?? item.name}  ($${item.price})`}
                  value={item.productSku}
                  checked={selectedRadio === item.productSku}
                  onChange={onRadioSelect}
                  name="selected-plan"
                  disabled={transitioning}
                />
                {item.productDetails && (
                  <ActionCard.SupportiveText className="!text-3 pl-7 !text-[#333F48]">
                    {productInfo.productDetails ?? item.productDetails}
                  </ActionCard.SupportiveText>
                )}
              </div>
            );
          })}
      </AuiContainer>
    </form>
  );
}
